import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, Info, Minus, RefreshCw } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  buildCommunityReportingView,
  formatCommunityDelta,
  formatRate,
  formatResponseTime,
  type CommunityDelta,
  type EngagementHandledView,
  type OutstandingItem,
  type OutstandingView,
  type ResponsivenessView,
} from '../../../lib/reporting/community';
import { CHANNEL_LABEL } from '../../../lib/inboxView';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import { StarRating } from '../../community';
import type { DateRange } from '../../../lib/reportingDateRange';
import type { InboxKind } from '../../../types/inbox';
import { cn } from '../../../lib/utils';
import { getSocialCommunity } from './api';
import { CommunityReviewsSection } from './CommunityReviewsSection';

// Community & Reputation tab — THE DIFFERENTIATOR. Sourced from OUR community inbox
// + review threads (Firestore, via the community module's barrel), NOT Zernio
// analytics — no competitor reporting tool can produce this. "Here's the service
// we delivered": how much we handled, how fast we responded, and the reputation
// we're building.
//
// Self-fetching (Radix unmounts inactive tabs, so it only queries when viewed).
// The data is our own Firestore — always current, so there's no as-of caveat;
// a small manual refresh re-reads it.

const KIND_LABEL: Record<InboxKind, string> = {
  comment: 'Comments',
  dm: 'Direct messages',
  review: 'Reviews',
};

// Our own data moves as the inbox is worked — a short staleTime keeps it fresh
// without hammering Firestore on every tab switch.
const COMMUNITY_STALE_MS = 60_000;

export function SocialCommunityTab({
  accountId,
  range,
}: {
  accountId: string;
  range: DateRange;
}) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['social-reporting-community', accountId, range.from, range.to],
    queryFn: () => getSocialCommunity(accountId, range),
    enabled: accountId !== '',
    staleTime: COMMUNITY_STALE_MS,
  });

  const view = useMemo(
    () => (query.data ? buildCommunityReportingView(query.data) : null),
    [query.data],
  );

  const refresh = () =>
    qc.invalidateQueries({
      queryKey: ['social-reporting-community', accountId, range.from, range.to],
    });

  if (query.isLoading) {
    return (
      <div className="py-16">
        <CenteredSpinner />
      </div>
    );
  }
  if (query.isError || !view || !query.data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-10 text-center">
        <p className="text-sm text-red-700">Couldn't load the community report.</p>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="mt-3 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Honesty framing — the managed-inbox scope, stated plainly. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            These figures cover the <span className="font-medium">managed inbox</span> for the
            period — conversations and reviews ingested for this account within our ingestion
            windows. An account connected mid-period, or items older than our recency cutoff, won't
            be included.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={query.isFetching}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw className={cn('h-3 w-3', query.isFetching && 'animate-spin')} aria-hidden />
          {query.isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <EngagementHandledSection view={view.engagement} />
      <ResponsivenessSection view={view.responsiveness} />
      <OutstandingSection view={view.outstanding} />
      {/* Facebook-review reputation — rendered only when this client has FB reviews.
          Google-review reputation now lives on the Google Business tab; FB reviews
          aren't "Google Business", so their reputation item stays here. */}
      {!view.facebookReviews.isEmpty && (
        <CommunityReviewsSection
          view={view.facebookReviews}
          title="Facebook reviews"
          subtitle="Facebook reviews received this period, and how we responded. Google reviews are on the Google Business tab."
          emptyMessage="No Facebook reviews received for this client in the selected period."
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// shared bits
// ---------------------------------------------------------------------------

function SectionShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export function DeltaChip({ delta }: { delta: CommunityDelta | null }) {
  const label = formatCommunityDelta(delta);
  if (!delta || !label || delta.direction === null) return null;
  const Icon =
    delta.direction === 'up' ? ArrowUpRight : delta.direction === 'down' ? ArrowDownRight : Minus;
  const tone =
    delta.direction === 'up'
      ? 'bg-emerald-50 text-emerald-700'
      : delta.direction === 'down'
        ? 'bg-red-50 text-red-700'
        : 'bg-slate-100 text-slate-500';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium',
        tone,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

export function StatBlock({
  label,
  value,
  delta,
  hint,
}: {
  label: string;
  value: string;
  delta?: CommunityDelta | null;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-slate-900">{value}</span>
        {delta && <DeltaChip delta={delta} />}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// §1 — Engagement handled
// ---------------------------------------------------------------------------

function EngagementHandledSection({ view }: { view: EngagementHandledView }) {
  if (view.isEmpty) {
    return (
      <SectionShell
        title="Engagement handled"
        subtitle="Everything the team actioned this period — comments, direct messages and reviews."
      >
        <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
          No inbox activity for this client in the selected period.
        </p>
      </SectionShell>
    );
  }
  return (
    <SectionShell
      title="Engagement handled"
      subtitle="Everything the team actioned this period — comments, direct messages and reviews."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,16rem)_1fr]">
        <div className="space-y-3">
          <div className="rounded-lg bg-kyck-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-kyck-700">Total handled</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums text-kyck-900">
                {view.total.current.toLocaleString()}
              </span>
              <DeltaChip delta={view.total} />
            </div>
            <p className="mt-1 text-xs text-kyck-700/70">
              vs {view.total.previous.toLocaleString()} last period
            </p>
          </div>

          {/* By kind */}
          <div className="space-y-1.5">
            {view.byKind.map((row) => (
              <div key={row.key} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{KIND_LABEL[row.key]}</span>
                <span className="font-medium tabular-nums text-slate-900">
                  {row.current.toLocaleString()}
                </span>
              </div>
            ))}
          </div>

          {/* By platform */}
          <div className="border-t border-slate-100 pt-2">
            {view.byChannel.map((row) => (
              <div key={row.key} className="flex items-center justify-between py-0.5 text-sm">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <ChannelIcon channel={row.key} className="h-3.5 w-3.5" />
                  {CHANNEL_LABEL[row.key]}
                </span>
                <span className="font-medium tabular-nums text-slate-900">
                  {row.current.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly volume — stacked bars by kind. */}
        <WeeklyVolumeChart view={view} />
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// §2 — Responsiveness
// ---------------------------------------------------------------------------

function ResponsivenessSection({ view }: { view: ResponsivenessView }) {
  const c = view.current;
  return (
    <SectionShell
      title="Responsiveness"
      subtitle="How quickly and how completely the team replied. A conversation counts as answered when it received a reply or was resolved; the median response time measures answered conversations only."
    >
      {view.isEmpty ? (
        <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
          No conversations to respond to in this period.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <StatBlock
            label="Response rate"
            value={formatRate(c.responseRate)}
            delta={view.responseRateDelta}
            hint={`${c.answered.toLocaleString()} of ${c.total.toLocaleString()} answered`}
          />
          <StatBlock
            label="Median response time"
            value={formatResponseTime(c.medianResponseMinutes)}
            delta={view.medianDelta}
            hint="Inbound → first reply"
          />
        </div>
      )}
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// §2b — Outstanding / unresponded worklist
// ---------------------------------------------------------------------------

// The count is ALL-KINDS (comments + DMs + reviews); the detail lists show only
// unresolved DMs and Reviews (comments are counted, not listed — they'd drown the
// signal). Read-only: a report, not the inbox (no reply actions here).
function OutstandingSection({ view }: { view: OutstandingView }) {
  if (view.isEmpty) {
    return (
      <SectionShell
        title="Outstanding"
        subtitle="Unresolved conversations still needing a reply."
      >
        <p className="rounded-md bg-emerald-50 px-3 py-6 text-center text-sm text-emerald-700">
          All caught up — nothing unresolved for this client this period.
        </p>
      </SectionShell>
    );
  }

  const hasDetail = view.dms.length > 0 || view.reviews.length > 0;
  const commentCount = view.byKind.find((r) => r.key === 'comment')?.current ?? 0;

  return (
    <SectionShell
      title="Outstanding"
      subtitle="Unresolved conversations still needing a reply. The count covers every kind; the lists below show the direct messages and reviews to action."
    >
      <div className="space-y-4">
        {/* Headline count (all kinds) + by-kind breakdown. */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-lg bg-amber-50 px-4 py-2">
            <span className="text-2xl font-semibold tabular-nums text-amber-900">
              {view.total.toLocaleString()}
            </span>
            <span className="ml-2 text-xs text-amber-700/80">unresolved (all kinds)</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {view.byKind.map((row) => (
              <span
                key={row.key}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600"
              >
                {KIND_LABEL[row.key]}: <span className="font-medium tabular-nums">{row.current}</span>
              </span>
            ))}
          </div>
        </div>

        {view.dms.length > 0 && (
          <OutstandingList title="Direct messages" items={view.dms} />
        )}
        {view.reviews.length > 0 && (
          <OutstandingList title="Reviews" items={view.reviews} />
        )}

        {commentCount > 0 && (
          <p className="text-xs text-slate-400">
            {commentCount.toLocaleString()} unresolved comment{commentCount === 1 ? '' : 's'}{' '}
            {commentCount === 1 ? 'is' : 'are'} included in the count above; work them in the
            Community inbox.
          </p>
        )}
        {!hasDetail && (
          <p className="rounded-md bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">
            The outstanding items this period are all comments — action them in the Community inbox.
          </p>
        )}
      </div>
    </SectionShell>
  );
}

function OutstandingList({ title, items }: { title: string; items: OutstandingItem[] }) {
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {title} <span className="text-slate-400">({items.length})</span>
      </p>
      <ul className="divide-y divide-slate-100">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-3 py-2.5">
            <ChannelIcon channel={item.channel} className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {item.kind === 'review' && item.rating !== null && <StarRating rating={item.rating} />}
                <span className="text-sm font-medium text-slate-800">{item.authorName}</span>
                <span className="text-xs text-slate-400">· {formatWhen(item.at)}</span>
              </div>
              {item.text ? (
                <p className="mt-0.5 line-clamp-3 text-sm text-slate-600">{item.text}</p>
              ) : (
                <p className="mt-0.5 text-sm italic text-slate-400">No message text captured</p>
              )}
            </div>
            {item.permalink && (
              <a
                href={item.permalink}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-xs font-medium text-kyck-600 hover:underline"
              >
                View
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// weekly volume chart (§1) — stacked bars by kind (comments / DMs / reviews)
// ---------------------------------------------------------------------------

const VOLUME_CONFIG: ChartConfig = {
  comment: { label: 'Comments', color: 'hsl(217 91% 60%)' },
  dm: { label: 'Direct messages', color: 'hsl(330 81% 60%)' },
  review: { label: 'Reviews', color: 'hsl(38 92% 50%)' },
};

function labelWeek(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function WeeklyVolumeChart({ view }: { view: EngagementHandledView }) {
  if (view.weekly.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-md bg-slate-50 text-sm text-slate-400">
        No items to chart.
      </div>
    );
  }
  return (
    <ChartContainer config={VOLUME_CONFIG} className="aspect-auto h-56 w-full">
      <BarChart data={view.weekly} margin={{ left: 4, right: 8, top: 4, bottom: 0 }} accessibilityLayer>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="weekStart"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={labelWeek}
        />
        <YAxis width={28} allowDecimals={false} tickLine={false} axisLine={false} />
        <ChartTooltip
          content={<ChartTooltipContent labelFormatter={(v) => `Week of ${labelWeek(String(v))}`} />}
        />
        <Bar dataKey="comment" stackId="v" fill="var(--color-comment)" radius={[0, 0, 0, 0]} isAnimationActive={false} />
        <Bar dataKey="dm" stackId="v" fill="var(--color-dm)" radius={[0, 0, 0, 0]} isAnimationActive={false} />
        <Bar dataKey="review" stackId="v" fill="var(--color-review)" radius={[2, 2, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ChartContainer>
  );
}
